import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

const layers = [
  {
    title: '数据层',
    items: ['实时行情API网关', '分布式数据存储', '时序数据库集群', '数据质量管理平台'],
    accent: 'border-l-primary-500',
    metricLabel: 'PEERS',
    metricTarget: 128,
    metricSuffix: '+',
    liveVal: 133.6,
    liveRange: 2,
    uptime: '99.97%',
  },
  {
    title: '计算层',
    items: ['GPU并行计算集群', '分布式回测引擎', '机器学习训练平台', '因子计算服务'],
    accent: 'border-l-accent-500',
    metricLabel: 'TFLOPS',
    metricTarget: 256,
    metricSuffix: '+',
    liveVal: 261.4,
    liveRange: 3,
    uptime: '99.93%',
  },
  {
    title: '应用层',
    items: ['策略管理控制台', '实时风险监控面板', '交易执行网关', '绩效归因分析系统'],
    accent: 'border-l-primary-500',
    metricLabel: 'TPS',
    metricTarget: 50,
    metricSuffix: 'K',
    liveVal: 51.2,
    liveRange: 0.5,
    uptime: '99.99%',
  },
];

function ArchitectureCard({ layer, index }: { layer: typeof layers[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [itemsReady, setItemsReady] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          const t = setTimeout(() => setItemsReady(true), 400 + index * 200);
          return () => clearTimeout(t);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [index]);

  const { formatted } = useCountUp({
    target: layer.metricTarget,
    duration: 2000,
    suffix: layer.metricSuffix,
    format: 'compact',
    enabled: visible,
  });

  const { formatted: liveFormatted, flash } = useLiveData({
    initial: layer.liveVal,
    range: layer.liveRange,
    decimals: 1,
    interval: 3200,
  });

  return (
    <div ref={ref} className={`data-panel border-l-2 ${layer.accent} p-6 md:p-8 group`}>
      {/* Title + status */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-heading font-bold text-foreground-100">{layer.title}</h3>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
          <span className="text-[9px] mono-data text-emerald-400/70">{layer.uptime}</span>
        </div>
      </div>

      {/* Items list with staggered animation */}
      <ul className="space-y-3 mb-5">
        {layer.items.map((item, i) => (
          <li
            key={item}
            className={`flex items-center gap-2.5 text-sm text-foreground-400 font-light transition-all duration-500 ${
              itemsReady ? 'arch-item-enter opacity-100 translate-x-0' : 'opacity-0 translate-x-[-8px]'
            }`}
            style={{ animationDelay: `${i * 120}ms` }}
          >
            <span className="w-1 h-1 rounded-full bg-primary-400/50 group-hover:bg-accent-400/60 transition-colors duration-300"></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Live metric bar */}
      <div className="flex items-center justify-between pt-4 border-t border-background-300/30">
        <div className="flex items-center gap-2">
          <span className="text-[9px] mono-data text-foreground-600 tracking-wide">{layer.metricLabel}</span>
          <span className="text-base mono-data font-bold text-foreground-200 tabular-nums">
            {visible ? formatted : '--'}
          </span>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-200/60 border transition-all duration-300 ${
          flash ? 'border-accent-500/40' : 'border-background-300/30'
        }`}>
          <span className={`w-1 h-1 rounded-full ${flash ? 'bg-accent-400' : 'bg-emerald-400'} animate-pulse-glow`}></span>
          <span className={`text-[9px] mono-data font-semibold tabular-nums ${flash ? 'text-accent-400 data-tick' : 'text-emerald-400'}`}>
            {liveFormatted}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function QuantArchitectureSection() {
  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Technology Architecture</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">技术架构</h2>
            <p className="mt-3 text-[10px] mono-data text-foreground-600">
              <span className="text-emerald-400/70">CLUSTER</span>
              <span className="mx-2 text-foreground-800">::</span>
              <span className="text-foreground-500">NODES  </span>
              <span className="text-accent-400/80">1,024</span>
              <span className="mx-2 text-foreground-800">|</span>
              <span className="text-foreground-500">REGION  </span>
              <span className="text-primary-400/80">HK-AP-SG 3-AZ</span>
              <span className="mx-2 text-foreground-800">|</span>
              <span className="text-foreground-500">STATUS  </span>
              <span className="text-emerald-400/80 animate-blink">HEALTHY</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {layers.map((layer, i) => (
              <ArchitectureCard key={layer.title} layer={layer} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}